import dayjs from 'dayjs';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR } from './reportDates';

/**
 * Calculates the number of days in a date range (inclusive)
 *
 * @param {Date} startDate - Start date of the range
 * @param {Date} endDate - End date of the range
 * @returns {number} Number of days in the range (inclusive)
 *
 * @example
 * const days = calculateDateRangeDays(new Date('2025-01-01'), new Date('2025-01-05'));
 * // Returns: 5
 */
export const calculateDateRangeDays = (startDate: Date, endDate: Date): number => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day') + 1;
};

/**
 * Checks if a custom time filter is applied
 *
 * @param {string} startHour - Start hour in format 'HH:mm:ss'
 * @param {string} endHour - End hour in format 'HH:mm:ss'
 * @returns {boolean} True if custom time filter is applied
 *
 * @example
 * hasCustomTimeFilter('09:00:00', '18:00:00'); // Returns: true
 * hasCustomTimeFilter(DEFAULT_START_HOUR, DEFAULT_END_HOUR); // Returns: false
 */
export const hasCustomTimeFilter = (startHour: string, endHour: string): boolean => {
  return startHour !== DEFAULT_START_HOUR || endHour !== DEFAULT_END_HOUR;
};

/**
 * Builds analytics properties from a block data request for report viewing
 *
 * Extracts analytics data such as report name, date range duration, time filter status,
 * and block identifier to be used in event logging for reports tracking.
 *
 * @param {BlockDataRequestDto} request - The block data request containing dates and filters
 * @param {string} blockKey - The unique identifier for the block being tracked
 * @param {string} reportName - The human-readable name of the report
 * @returns {Object} Analytics properties object
 * @returns {string} returns.report_name - The report name
 * @returns {string} returns.block_key - The block identifier
 * @returns {string} returns.report_version - Version of the report (always 'new')
 * @returns {number} returns.date_range_days - Number of days in the date range (inclusive)
 * @returns {boolean} returns.has_time_filter - Whether a custom time range filter is applied
 *
 * @example
 * const analyticsData = buildReportBlockAnalytics(
 *   {
 *     startDate: new Date('2025-01-01'),
 *     endDate: new Date('2025-01-05'),
 *     startHour: '09:00:00',
 *     endHour: '18:00:00',
 *     // ... other fields
 *   },
 *   'sales_table',
 *   'Sales Report'
 * );
 * // Returns: {
 * //   report_name: 'Sales Report',
 * //   block_key: 'sales_table',
 * //   report_version: 'new',
 * //   date_range_days: 5,
 * //   has_time_filter: true
 * // }
 */
export const buildReportBlockAnalytics = (request: BlockDataRequestDto, blockKey: string, reportName: string) => {
  const dateRangeDays = calculateDateRangeDays(request.startDate, request.endDate);
  const hasTimeFilter = hasCustomTimeFilter(request.startHour, request.endHour);

  return {
    report_name: reportName,
    block_key: blockKey,
    report_version: 'new',
    date_range_days: dateRangeDays,
    has_time_filter: hasTimeFilter,
  };
};

/**
 * Builds analytics properties for report export events
 *
 * Tracks export performance and configuration including report name,
 * export duration, and date range being exported.
 *
 * @param {BlockDataRequestDto} request - The block data request containing dates and filters
 * @param {string} reportName - The human-readable name of the report being exported
 * @param {number} loadTimeMs - Time taken to generate the export in milliseconds
 * @returns {Object} Analytics properties object for export events
 * @returns {string} returns.report_name - The report name
 * @returns {number} returns.load_time_ms - Export generation time in milliseconds
 * @returns {number} returns.date_range_days - Number of days in the date range (inclusive)
 *
 * @example
 * const startTime = Date.now();
 * // ... export logic ...
 * const loadTimeMs = Date.now() - startTime;
 * const analyticsData = buildReportExportAnalytics(
 *   {
 *     startDate: new Date('2025-01-01'),
 *     endDate: new Date('2025-01-05'),
 *     // ... other fields
 *   },
 *   'Sales Report',
 *   loadTimeMs
 * );
 * // Returns: {
 * //   report_name: 'Sales Report',
 * //   load_time_ms: 1234,
 * //   date_range_days: 5
 * // }
 */
export const buildReportExportAnalytics = (request: BlockDataRequestDto, reportName: string, loadTimeMs: number) => {
  const dateRangeDays = calculateDateRangeDays(request.startDate, request.endDate);

  return {
    report_name: reportName,
    load_time_ms: loadTimeMs,
    date_range_days: dateRangeDays,
  };
};
